import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SnackBarData } from 'src/app/common/components/snackbar/model/snackbar.model';
import { SnackBarService } from 'src/app/common/components/snackbar/snackbar.service';
import { FormBuilder, FormControl, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { ConfirmDialogComponent, ConfirmDialogModel } from 'src/app/common/components/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { DirtyCheck } from 'src/app/common/guards/form-deactivate.guard';
import { UserGroupModel } from '../model/usergroup.model';
import { UserGroupService } from '../usergroup.service';
import { UserGroupMenuModel } from '../model/usergroupmenu.model';
import { PermissionService } from 'src/app/auth/permission.service';

@Component({
  selector: 'app-usergroup-edit',
  templateUrl: './usergroup-edit.component.html',
  styleUrl: './usergroup-edit.component.scss',
  standalone: false
})
export class UserGroupEditComponent implements DirtyCheck {

  title: string = "New User Group";
  editForm: FormGroup;
  userGroupInfo: UserGroupModel = {};
  userGroupMenuInfo: UserGroupMenuModel = {};
  isFormDirty: boolean = false;
  userGroupList: UserGroupModel[] = [];
  permission: any;

  constructor(private router: Router,
    private builder: FormBuilder,
    private route: ActivatedRoute,
    private userGroupService: UserGroupService,
    private snackBarService: SnackBarService,
    private permissionService: PermissionService,
    public dialog: MatDialog) {

    let userGroupId = this.route.snapshot.paramMap.get('id');

    this.editForm = this.builder.group({
      code: this.builder.control(this.userGroupInfo.code, [Validators.required, Validators.maxLength(10)]),
      name: this.builder.control(this.userGroupInfo.name, [Validators.required, Validators.maxLength(50)]),
      description: this.builder.control(this.userGroupInfo.description),
      user_type: this.builder.group({
        hq: [false],
        shop: [false],
        areaManager: [false],
      }, { validators: this.userTypeValidator }),
      menu: this.builder.group({
        is_dashboard_page_visible: [this.userGroupInfo.menu?.is_dashboard_page_visible ?? false],
        is_people_page_visible: [this.userGroupInfo.menu?.is_people_page_visible ?? false],
        is_utilities_page_visible: [this.userGroupInfo.menu?.is_utilities_page_visible ?? false],
        is_reports_page_visible: [this.userGroupInfo.menu?.is_reports_page_visible ?? false],
        is_settings_page_visible: [this.userGroupInfo.menu?.is_settings_page_visible ?? false],
        is_manager_page_visible: [this.userGroupInfo.menu?.is_manager_page_visible ?? false],
        is_user_page_visible: [this.userGroupInfo.menu?.is_user_page_visible ?? false],
        is_userdefined_page1_visible: [this.userGroupInfo.menu?.is_userdefined_page1_visible ?? false],
        is_userdefined_page2_visible: [this.userGroupInfo.menu?.is_userdefined_page2_visible ?? false],
        is_userdefined_page3_visible: [this.userGroupInfo.menu?.is_userdefined_page3_visible ?? false],
        is_attendance_page_visible: [this.userGroupInfo.menu?.is_attendance_page_visible ?? false],
      }),
    });

    this.editForm.valueChanges.subscribe(() => {
      this.isFormDirty = true;
    });

    if (userGroupId !== null && parseInt(userGroupId) !== 0) {
      this.setuserGroupInfo(userGroupId);
    }
    this.setupUserTypeLogic();
    this.permission = this.permissionService.getRowPermission(this.permissionService.getPagePermission('user-group'), userGroupId);
  }
  ngOnInit(): void {
  }

  userTypeValidator(group: AbstractControl) {
    const hq = group.get('hq')?.value;
    const shop = group.get('shop')?.value;
    const areaManager = group.get('areaManager')?.value;
    if (!hq && !shop && !areaManager) {
      return { required: true };
    }
    return null;
  }

  setupUserTypeLogic() {
    const userTypeGroup = this.editForm.get('user_type') as FormGroup;

    userTypeGroup.get('areaManager')?.valueChanges.subscribe(isChecked => {
      if (isChecked) {
        userTypeGroup.patchValue({ hq: false, shop: false }, { emitEvent: false });
      }
    });

    userTypeGroup.get('hq')?.valueChanges.subscribe(isChecked => {
      if (isChecked) {
        userTypeGroup.patchValue({ areaManager: false }, { emitEvent: false });
      }
    });

    userTypeGroup.get('shop')?.valueChanges.subscribe(isChecked => {
      if (isChecked) {
        userTypeGroup.patchValue({ areaManager: false }, { emitEvent: false });
      }
    });

    // If logged in as SHOP user, force the shop checkbox to be ticked
    if (this.isShopUser) {
      userTypeGroup.patchValue({ shop: true }, { emitEvent: false });
    }
  }

  get showMenuCheckboxes() {
    return this.editForm.get('user_type.shop')?.value;
  }

  getUserTypeValue(): number {
    const ut = this.editForm.get('user_type')?.value;
    if (ut.areaManager) return 3;
    if (ut.hq && ut.shop) return 2;
    if (ut.hq) return 0;
    if (ut.shop) return 1;
    return -1;
  }

  /**
   * Dirtyness check
   * @returns 
   */
  isDirty(): boolean {
    return this.isFormDirty;
  }

  /**
   * checks the form is edited
   */
  navBack(): void {

    this.router.navigate(['/user/user-group']);

  }

  /**
   * If tas is is null set an empty area info
   * if   id is not null call api to rtrive the info
   * @param userGroupId 
   */
  setuserGroupInfo(userGroupId: any): void {

    let response = this.userGroupService.getById(userGroupId).subscribe({
      next: (response) => {
        if (response.status == 'SUCCESS') {
          this.userGroupInfo = response.data.user_group;
          const menu = response.data.user_group.menu;

          // SET USER TYPE
          this.editForm.patchValue(this.userGroupInfo);
          const userTypeGroup = this.editForm.get('user_type') as FormGroup;

          if (this.userGroupInfo.user_type == 3) {
            userTypeGroup.patchValue({ hq: false, shop: false, areaManager: true });
          } else if (this.userGroupInfo.user_type == 2) {
            userTypeGroup.patchValue({ hq: true, shop: true, areaManager: false });
          } else if (this.userGroupInfo.user_type == 1) {
            userTypeGroup.patchValue({ hq: false, shop: true, areaManager: false });
          } else {
            userTypeGroup.patchValue({ hq: true, shop: false, areaManager: false });
          }

          this.title = this.getTitle();
          this.isFormDirty = false;
        } else {
          let snackBarData: SnackBarData = {
            message: response.message,
            title: 'Failed!!!',
            type: 'error'
          }
          this.snackBarService.openSnackBar(snackBarData);
        }
      },
      error: (error) => {
        let snackBarData: SnackBarData = {
          message: 'Failed to get data from server. Connection to the server failed.',
          title: 'Failed!!!',
          type: 'error'
        }
        this.snackBarService.openSnackBar(snackBarData);
      }
    });
  }

  /**
   * 
   * @returns  the title for the dialog
   * sine the same dialog is used for edit and new, dynamically decide the title
   */
  getTitle(): string {
    let title = (this.userGroupInfo.id == 0) ? "New User Group" : "Editing : " + this.userGroupInfo.name;
    return title;
  }

  /**
     * On save
     * if the details are valid call the api to update the changes.
     */
  save(): void {

    if (this.editForm.valid) {

      const form = this.editForm.value;


      this.editForm.markAllAsTouched();
      Object.assign(this.userGroupInfo, this.editForm.value);
      this.userGroupInfo.user_type = this.getUserTypeValue();
      this.userGroupInfo.menu = this.isShopUser ? undefined : this.editForm.value.menu;

      this.userGroupService.update(this.userGroupInfo).subscribe({
        next: (response) => {
          if (response.status == 'SUCCESS') {
            // handle success
            this.isFormDirty = false;
            let snckBarData: SnackBarData = {
              type: 'success',
              title: 'Saved',
              message: 'The record for ' + this.userGroupInfo.name + " has been updated succesfully."
            }
            this.snackBarService.openSnackBar(snckBarData);
            this.router.navigate(['/user/user-group']);
          } else if (response.code === 'DATA_CHANGED_AT_SERVER') {
            let snackBarData: SnackBarData = {
              message: 'This record has been updated by another user. Please reload the data to see the latest changes.',
              title: 'Conflict Detected',
              type: 'error'
            }
            this.snackBarService.openSnackBar(snackBarData);
            // Refresh the data to show the latest version from server
            if (this.userGroupInfo.id) {
              this.setuserGroupInfo(this.userGroupInfo.id);
            }
          } else {
            let snackBarData: SnackBarData = {
              message: response.message,
              title: 'Failed!!!',
              type: 'error'
            }
            this.snackBarService.openSnackBar(snackBarData);
          }
        },
        error: (error) => {
          // handle error
          let snackBarData: SnackBarData = {
            message: 'Failed to update. Connection to the server failed',
            title: 'Failed!!!',
            type: 'error'
          }
          this.snackBarService.openSnackBar(snackBarData);
        }
      });

    } else {
      this.editForm.markAllAsTouched();
    }
  }


  get isShopUser(): boolean {
    const user = this.permissionService.getCurrentUser();
    return user?.user_type === 'SHOP';
  }
}

